import { useCallback, useEffect, useState } from 'react';
import { Note, PasswordChange, Spinner } from '@wyocspay/ui';
import { ApiError, api, getToken, setToken, setUnauthorisedHandler, type Overview } from './api.js';
import { ConnectProvider } from './connect.js';
import { LocaleProvider } from './locale.js';
import { setLocaleCountry } from './format.js';
import { Layout, type NavEntry } from './Layout.js';
import { Login } from './Login.js';
import { AcceptInvite, FeaturesView } from './Features.js';
import { PackagesView } from './Packages.js';
import { AccountDetailsView } from './AccountDetails.js';
import { NewPaymentView } from './NewPayment.js';
import { InvoicesView } from './Invoices.js';
import { TicketsView } from './Tickets.js';
import { PaymentMethodsView } from './PaymentMethods.js';
import { MailSettingsView } from './MailSettings.js';
import { TerminalsView } from './Terminals.js';
import { ApiDocsView } from './ApiDocs.js';
import { Dac7View } from './Dac7.js';
import { TaxRegistrationView } from './TaxRegistration.js';
import { ReportsView } from './Reports.js';
import { DocumentsView } from './Documents.js';
import { VerificationView } from './Verification.js';
import { MachinePaymentsView } from './MachinePayments.js';
import { Onboarding, OnboardingScreen } from './Onboarding.js';
import { accountNeed, pollInterval, type AccountNeed } from './account-state.js';
import {
  AccountSettingsView,
  BalancesView,
  DisputesView,
  PayoutsView,
  StripePaymentsView,
} from './money-views.js';
import {
  ActivityView,
  DeliveriesView,
  DevelopersView,
  OverviewView,
  PaymentDetailView,
  PaymentsView,
} from './views.js';

const NAV: NavEntry[] = [
  { heading: 'Money' },
  { key: 'overview', label: 'Overview' },
  { key: 'payments', label: 'Payments' },
  { key: 'new-payment', label: 'New payment' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'balances', label: 'Balances' },
  { key: 'payouts', label: 'Payouts' },
  { heading: 'Accept payments' },
  { key: 'payment-methods', label: 'Payment methods' },
  { key: 'terminals', label: 'Card terminals' },
  { key: 'stripe-payments', label: 'Charges & refunds' },
  { key: 'disputes', label: 'Disputes' },
  { heading: 'Account' },
  { key: 'onboarding', label: 'Verification' },
  { key: 'features', label: 'Capabilities' },
  { key: 'packages', label: 'Fee packages' },
  { key: 'account-details', label: 'Your details' },
  { key: 'settings', label: 'Business details' },
  { key: 'dac7', label: 'EU reporting' },
  { key: 'tax-registration', label: 'Tax registration' },
  { key: 'documents', label: 'Documents' },
  { key: 'verification', label: 'Verification' },
  { heading: 'Developers' },
  { key: 'machine-payments', label: 'Machine payments' },
  { key: 'developers', label: 'API & webhooks' },
  { key: 'api-docs', label: 'API reference' },
  { key: 'deliveries', label: 'Deliveries' },
  { heading: 'Business' },
  { key: 'reports', label: 'Reports & exports' },
  { key: 'mail', label: 'Email' },
  { key: 'tickets', label: 'Support' },
  { key: 'activity', label: 'Activity log' },
];

/**
 * The navigation, minus anything there is no reason to offer.
 *
 * "Verification" is the only conditional item, and it is absent from a healthy
 * account entirely rather than shown greyed or ticked. A merchant who has
 * finished has no use for a permanent link to a form asking them to finish: it
 * reads as unfinished business every time they look at the sidebar.
 *
 * It comes back on its own the moment `accountNeed` says Stripe wants something,
 * because the nav is derived from that one value rather than kept in step by
 * hand. Nothing is hidden that the merchant needs - the page stays routable, so
 * a bookmark or a link from the overview still opens it.
 */
function navFor(need: AccountNeed, gateStep: string | null): typeof NAV {
  let nav = NAV;
  // Stripe's own verification link goes once Stripe is satisfied.
  if (need === null) {
    nav = nav.filter((item) => !('key' in item) || item.key !== 'onboarding');
  }
  // The platform's own verification comes after Stripe's and only while it is
  // unfinished: a verified account has no reason to see a link to a form asking
  // it to verify, and before Stripe is done it is not that account's turn yet.
  const showVerification = need === null && gateStep !== null && gateStep !== 'open';
  if (!showVerification) {
    nav = nav.filter((item) => !('key' in item) || item.key !== 'verification');
  }
  return nav;
}

const TITLES: Record<string, [string, string]> = {
  overview: ['Overview', 'Account health, volume and settlement at a glance'],
  payments: ['Payments', 'The WyoCSPay ledger - attempts and audit trail per payment'],
  'new-payment': ['New payment', 'Create a charge and send the customer a link'],
  invoices: ['Invoices', 'Write, issue, collect and credit invoices in your own currency'],
  balances: ['Balances', 'Funds on your Stripe account, and when they settle'],
  payouts: ['Payouts', 'Bank settlement history and schedule'],
  'payment-methods': ['Payment methods', 'What your customers can pay with'],
  terminals: ['Card terminals', 'Taking payments in person, on a phone or on a Stripe card reader'],
  'stripe-payments': ['Charges & refunds', "Stripe's record, with refunds and disputes built in"],
  disputes: ['Disputes', 'Chargebacks, evidence deadlines and outcomes'],
  onboarding: ['Verification', 'Stripe collects and verifies your business details'],
  features: ['Capabilities', 'What your account can do, and how to ask for more'],
  packages: ['Fee packages', 'Pay a flat monthly fee instead of a fee on every payment'],
  'account-details': [
    'Your details',
    'Everything we hold about your business, what Stripe holds instead, and how to change either',
  ],
  settings: ['Business details', 'Your logo, bank accounts, business details and verification documents'],
  dac7: ['EU reporting', 'What EU platform reporting rules ask us to hold about you, and what we would report'],
  'tax-registration': ['Tax registration', 'Registering your shop with your tax authority, with Stripe as the declared processor'],
  documents: ['Documents', 'Your agreements and legal documents, filled in from your account'],
  verification: ['Verification', 'Verify your business details and documents to activate your account'],
  developers: ['API & webhooks', 'Keys, endpoints and signing secrets'],
  'machine-payments': ['Machine payments', 'Create merchant-scoped MPP and x402 resources and links after approval'],
  'api-docs': ['API reference', 'Every endpoint, with examples, in your language'],
  deliveries: ['Deliveries', 'Every webhook we sent you, and what came back'],
  reports: ['Reports & exports', 'Statements for your books and for your accountant, as PDF, Excel or CSV'],
  mail: ['Email', 'Send payment links from your own address, through your own mail server'],
  tickets: ['Support', 'Ask us anything, attach what you have, keep the thread'],
  activity: ['Activity log', 'Every change made to this account'],
};

export function App() {
  // Invitation acceptance is the only unauthenticated route in this panel.
  const inviteToken = /^\/invite\/([A-Za-z0-9_-]+)/.exec(window.location.pathname)?.[1];

  const [authed, setAuthed] = useState<boolean | null>(null);
  /**
   * PCI DSS 8.3.9. The server has already decided this session may do nothing
   * but set a new password; this only makes the panel show the right screen
   * instead of a dashboard where every request returns 403.
   */
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [user, setUser] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);
  const [settlementCurrency, setSettlementCurrency] = useState<string | null>(null);
  const [view, setView] = useState('overview');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  /**
   * The onboarding gate, read once the merchant is signed in. When it is being
   * enforced and not yet passed, the panel is confined to the verification flow
   * (and, at the signing step, the documents view) until it clears.
   */
  const [gate, setGate] = useState<{ enforced: boolean; step: string } | null>(null);
  const gated = !!gate && gate.enforced && gate.step !== 'open';
  const gateAllows = (v: string) => v === 'verification' || (gate?.step === 'signing' && v === 'documents');
  /**
   * Which tab "Business details" should open on, when something sent them there.
   *
   * Only set by the details page below, and cleared by any ordinary navigation,
   * so choosing the section from the sidebar still lands on its first tab.
   */
  const [settingsTab, setSettingsTab] = useState<string | null>(null);

  /**
   * The account's Stripe state, and the thing the gate below is decided on.
   *
   * `null` means "not asked yet". It is deliberately not initialised to
   * anything optimistic: a merchant reloading the page must not see one frame
   * of a dashboard before the gate appears, and the only way to guarantee that
   * is to have nothing to render until the server has answered.
   */
  const [account, setAccount] = useState<Overview['merchant'] | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  /** Arrived at verification to provide something, rather than to look. */
  const [verifyIntent, setVerifyIntent] = useState(false);

  /**
   * Ask the server where onboarding stands.
   *
   * `askStripe` makes the server read the connected account from Stripe first
   * and write down what it says. That is the authoritative answer and it costs a
   * round trip to Stripe plus an audit entry, so it is reserved for the moments
   * that deserve it: the embedded component signalling that the merchant has
   * finished, and the merchant pressing the button themselves. Everything else
   * reads our own record, which the `account.updated` webhook keeps current.
   *
   * Nothing here decides anything from the client's side. `onboarding_complete`
   * is computed by the server from `charges_enabled`, which only Stripe sets.
   */
  const readAccount = useCallback(async (askStripe: boolean): Promise<void> => {
    try {
      if (askStripe) {
        // A failure here is not fatal: the read below still returns whatever the
        // webhook last recorded, which is usually the same answer.
        await api.connectRefresh().catch(() => undefined);
      }
      const overview = await api.overview();
      setAccount(overview.merchant);
      setAccountError(null);
    } catch (err) {
      // A 401 has already sent us back to sign-in through the handler below.
      // A gated merchant gets 403 `onboarding_required` here; that is not an
      // error to show - the verification view takes over the panel instead.
      if (err instanceof ApiError && err.code === 'onboarding_required') return;
      setAccountError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    async function check(): Promise<void> {
      if (!getToken()) {
        setAuthed(false);
        return;
      }
      try {
        const me = await api.me();
        setUser(me.user.name);
        setLocaleCountry(me.user.country);
        setCountry(me.user.country);
        setDefaultCurrency(me.user.default_currency);
        setSettlementCurrency(me.user.settlement_currency);
        setMustChangePassword(me.session.password_change_required);
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    }
    void check();
  }, []);

  /**
   * Read the onboarding gate once signed in, and again on each navigation so it
   * reflects progress made in the verification flow. When it is enforced and not
   * yet passed, `gated` confines the panel to the verification view (and the
   * documents view at the signing step).
   */
  useEffect(() => {
    if (!authed) return;
    let live = true;
    api
      .onboarding()
      .then((s) => {
        if (live) setGate({ enforced: s.enforced, step: s.step });
      })
      .catch(() => {
        if (live) setGate(null);
      });
    return () => {
      live = false;
    };
  }, [authed, view]);

  useEffect(() => {
    if (gated && !gateAllows(view)) setView('verification');
  }, [gated, view]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Read the account on every mount, from the server, always.
   *
   * This is what makes the gate hold on a reload, on a bookmark, on a tab that
   * was open before onboarding was finished and on a URL typed by hand. A
   * reloaded page starts with an empty client and a session token, so anything
   * the sign-in response happened to say about onboarding is gone by then;
   * carrying it in memory would mean the gate held once and never again.
   */
  useEffect(() => {
    if (authed !== true || mustChangePassword) return;
    void readAccount(false);
  }, [authed, mustChangePassword, readAccount]);

  /**
   * The one value the whole panel reads. `null` means Stripe wants nothing.
   *
   * Every surface that used to decide this for itself - the gate, the nav, the
   * overview banner, the verification page - now derives from here, so they
   * cannot contradict each other the way they did.
   */
  const need = account === null ? null : accountNeed(account);
  const hasAccount = account !== null;

  /**
   * Keep asking, and keep asking after it is finished.
   *
   * Stripe's own signal arrives through the embedded component's exit callback,
   * and that is the fast path. This is the slow one, and it exists because
   * verification does not always finish while the merchant is looking at the
   * form - Stripe can take minutes, or come back the next day - and a merchant
   * who has been left staring at a screen that is no longer true will reload,
   * find it unchanged, and open a ticket.
   *
   * This used to stop the instant charges were enabled, which left the panel
   * blind to anything Stripe asked for afterwards. Stripe raises those without
   * switching charges off, so no error would have prompted a reload either: the
   * merchant would have found out when their payouts were held. It now runs for
   * as long as the panel is open, slowly on a healthy account and quickly on one
   * with something outstanding - `pollInterval` owns that choice.
   *
   * Every dependency is a primitive, so a poll that returns the same answer does
   * not tear the timer down and build another one.
   */
  useEffect(() => {
    if (authed !== true || mustChangePassword || !hasAccount) return;
    const timer = setInterval(() => {
      void readAccount(false);
    }, pollInterval(need));
    return () => clearInterval(timer);
  }, [authed, mustChangePassword, hasAccount, need, readAccount]);

  /** Any 401 returns to sign-in rather than leaving an error on screen. */
  useEffect(() => {
    setUnauthorisedHandler(() => {
      setAuthed(false);
      setUser('');
      setPaymentId(null);
      /**
       * The account goes with the session.
       *
       * Left behind, it would decide the gate for whoever signs in next: an
       * expired session followed by a sign-in as a different, unverified
       * merchant would show that merchant a dashboard for the frame between
       * authentication succeeding and the account being read back.
       */
      setAccount(null);
    });
    return () => setUnauthorisedHandler(null);
  }, []);

  if (inviteToken) return <AcceptInvite token={inviteToken} />;

  if (authed === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!authed) {
    return (
      <Login
        onDone={async () => {
          const me = await api.me();
          setUser(me.user.name);
          setLocaleCountry(me.user.country);
          setCountry(me.user.country);
          setDefaultCurrency(me.user.default_currency);
          setSettlementCurrency(me.user.settlement_currency);
          setMustChangePassword(me.session.password_change_required);
          setAuthed(true);
        }}
      />
    );
  }

  /**
   * Stands in front of the whole panel, not beside it. The server refuses every
   * other endpoint while this is outstanding, so there is nothing behind this
   * screen to reach.
   */
  if (mustChangePassword) {
    return (
      <PasswordChange
        reason="expired"
        onSubmit={async (current, next) => {
          await api.changePassword(current, next);
          // The change revoked every session, including this one.
          setToken(null);
          setMustChangePassword(false);
          setAuthed(false);
        }}
      />
    );
  }

  const [title, subtitle] = paymentId
    ? ['Payment', 'Attempts, state changes and where each one came from']
    : (TITLES[view] ?? ['', '']);

  const logout = async (): Promise<void> => {
    await api.logout().catch(() => undefined);
    setToken(null);
    setAuthed(false);
    setAccount(null);
  };

  return (
    // One Connect instance for the whole panel: every embedded Stripe surface
    // below shares a single AccountSession. It wraps the onboarding gate as
    // well as the panel, so lifting the gate does not tear the Stripe instance
    // down and build a new one on the other side.
    <LocaleProvider country={country} settlementCurrency={settlementCurrency}>
      <ConnectProvider>
      {account === null ? (
        /**
         * Nothing at all until the server has said where onboarding stands.
         *
         * This is the frame that would otherwise show a dashboard to a merchant
         * who is not allowed one. It is not a nicety: the panels behind it would
         * each fire a request that the account cannot satisfy, so the merchant
         * would see the dashboard flash and then fill with errors, which is how
         * this looked before.
         */
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
          {accountError ? (
            <Note tone="danger" title="We could not read your account">
              {accountError}
            </Note>
          ) : (
            <Spinner label="Loading your account…" />
          )}
        </div>
      ) : !account.onboarding_complete ? (
        /**
         * The gate.
         *
         * Modelled on `mustChangePassword` in the admin console: a condition
         * evaluated in front of the whole application on every render, not a
         * redirect run once at the end of sign-in. There is no navigation
         * rendered behind it, so there is nothing to click, nothing to bookmark
         * back into and nothing implying the merchant is missing a section they
         * could otherwise reach. A reload re-reads the condition from the server
         * before anything is drawn, and an open tab picks it up on its next poll.
         *
         * It lifts when the server says `onboarding_complete`, which the server
         * computes from `charges_enabled` - Stripe's decision, not ours. There
         * is no client-side way past it, and none is needed: the API refuses the
         * money endpoints for an unverified account regardless of what any panel
         * chooses to render.
         */
        <OnboardingScreen
          account={account}
          user={user}
          onLogout={() => void logout()}
          onRecheck={readAccount}
        />
      ) : (
      <Layout
        nav={navFor(need, gate?.step ?? null)}
        current={view}
        onNavigate={(key) => {
          setPaymentId(null);
          setSettingsTab(null);
          // Reaching verification from the menu is not the same as being sent
          // there to provide something, so the form does not force itself open.
          setVerifyIntent(false);
          setView(key);
        }}
        title={title}
        subtitle={subtitle}
        user={user}
        onLogout={() => void logout()}
      >
        {paymentId ? (
          <PaymentDetailView id={paymentId} onBack={() => setPaymentId(null)} />
        ) : view === 'overview' ? (
          <OverviewView onOpen={setPaymentId} onOpenVerification={() => setView('onboarding')} />
        ) : view === 'payments' ? (
          <PaymentsView onOpen={setPaymentId} />
        ) : view === 'new-payment' ? (
          <NewPaymentView
            onOpen={setPaymentId}
            defaultCurrency={defaultCurrency ?? settlementCurrency ?? 'usd'}
          />
        ) : view === 'invoices' ? (
          <InvoicesView />
        ) : view === 'balances' ? (
          <BalancesView />
        ) : view === 'payouts' ? (
          <PayoutsView />
        ) : view === 'stripe-payments' ? (
          <StripePaymentsView />
        ) : view === 'disputes' ? (
          <DisputesView />
        ) : view === 'onboarding' ? (
          /**
           * Still reachable after the gate has lifted, and deliberately so.
           *
           * Stripe can ask for more later - a document that has expired, details
           * it did not need until volume grew - without switching charges off.
           * That does not raise the gate, and the merchant needs somewhere to
           * answer it inside a panel they can still use.
           */
          <Onboarding account={account} onRecheck={readAccount} openForm={verifyIntent} />
        ) : view === 'terminals' ? (
          /**
           * Given the navigator for the same reason the two views below have
           * one: when the answer to "why can I not register a phone" is a
           * capability that has to be asked for, the useful thing this page can
           * do is take the merchant to where asking happens.
           */
          <TerminalsView onNavigate={setView} />
        ) : view === 'mail' ? (
          <MailSettingsView />
        ) : view === 'features' ? (
          <FeaturesView />
        ) : view === 'packages' ? (
          <PackagesView />
        ) : view === 'account-details' ? (
          /**
           * The only view that navigates on the merchant's behalf.
           *
           * Half of what it lists is not ours to change, so the useful thing it
           * can do is take somebody to the Stripe surface that does own the
           * field - including the right tab, since "Business details" has more
           * than one and landing on the wrong one is how a merchant concludes
           * the thing they were sent for is not there.
           */
          <AccountDetailsView
            onNavigate={(next, tab) => {
              setSettingsTab(tab ?? null);
              setView(next);
            }}
          />
        ) : view === 'settings' ? (
          <AccountSettingsView initialTab={settingsTab} />
        ) : view === 'dac7' ? (
          <Dac7View />
        ) : view === 'tax-registration' ? (
          <TaxRegistrationView />
        ) : view === 'developers' ? (
          <DevelopersView />
        ) : view === 'machine-payments' ? (
          <MachinePaymentsView />
        ) : view === 'deliveries' ? (
          <DeliveriesView />
        ) : view === 'payment-methods' ? (
          /**
           * Given the navigator for the same reason "Your details" has one:
           * when the answer to "why can I not offer this" is the account's own
           * verification, the useful thing the page can do is take the merchant
           * to where that is finished rather than name the screen and stop.
           */
          <PaymentMethodsView
            /**
             * Anyone sent from here to verification is being sent to hand
             * something over, so the form opens rather than the settled panel.
             * The account can be clean while one capability is not.
             */
            onNavigate={(next) => {
              if (next === 'onboarding') setVerifyIntent(true);
              setView(next);
            }}
          />
        ) : view === 'api-docs' ? (
          <ApiDocsView />
        ) : view === 'reports' ? (
          <ReportsView />
        ) : view === 'documents' ? (
          <DocumentsView />
        ) : view === 'verification' ? (
          <VerificationView onGoToDocuments={() => setView('documents')} />
        ) : view === 'tickets' ? (
          <TicketsView />
        ) : (
          <ActivityView />
        )}
      </Layout>
      )}
      </ConnectProvider>
    </LocaleProvider>
  );
}
